import { useState, useEffect, useMemo } from 'react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AddIcon from '@mui/icons-material/Add';
import { Badge, Box, IconButton, Popover, Stack, useMediaQuery, useTheme } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import TabsUI from '../Interface/TabsUI';

import TodoContent from './TodoContent';
import simpletaskService from '../../service/simpletask.service';
import useUsers from '../../context/UsersContext';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import useMenus from '../../context/MenuContext';
import useView from '../../hooks/useView';
import TodoFilters from './TodoFilters';
import SimpleTaskPhoneFormDialog from '../SimpleTaskPhone/SimpleTaskPhoneFormDialog';

const MyTodoTable = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { menus, getMenus, getMenuSubMenuId } = useMenus();
  const { getUser, user, getRole } = useUsers();
  const myTodoFilter = useView('myTodoFilter', user);
  const [activeTab, setActiveTab] = useState(0);
  const [roles, setRoles] = useState({});
  const [submenuId, setSubmenuId] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [prioValue, setPrioValue] = useState('');
  const [taskChanged, setTaskChanged] = useState(false);
  const [addTaskDialog, setAddTaskDialog] = useState({ open: false, data: {} });

  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    status: [],
    priority: [],
    companyLocation: [],
  });

  const [formVal, setFormVal] = useState({
    statusId: [],
    priorId: '',
    simpleTaskName: '',
    companyLocationId: '',
  });

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();
  }, []);

  useEffect(() => {
    setSubmenuId(getMenuSubMenuId('simpleTask'));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      const roles = getRole('MYTODO_SEE_ONLY_OWN');

      setRoles(roles);

      const companyLocationId = user?.companyLocation?.id;

      if (companyLocationId) {
        setFormVal((prev) => ({
          ...prev,
          companyLocationId,
        }));
      }
    }
  }, [user]);

  useEffect(() => {
    if (submenuId) initDefaultValues();
  }, [submenuId]);

  useEffect(() => {
    if (Object.keys(myTodoFilter).length > 0) {
      setFormVal((prev) => ({
        ...prev,
        statusId: myTodoFilter.status || [],
        priorId: myTodoFilter.priority?.value || '',
      }));

      setPrioValue(myTodoFilter.priority || '');
    }
  }, [myTodoFilter]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(submenuId)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .priorityToForm(submenuId)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyLocationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          status: resp[0],
          priority: resp[1],
          companyLocation: resp[2],
        };

        const filteredStatus = aData.status.statusItems.filter((item) => item.onMyTodo);

        const acOptions = {
          status: generateAutocompleteItems(filteredStatus, 'itemName', 'itemId'),
          priority: generateAutocompleteItems(aData.priority.priorityListItems, 'itemName', 'itemId'),
          companyLocation: generateAutocompleteItems(aData.companyLocation, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        //hideLoader()
      });
  };

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  const handleOpenFilters = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setFilterAnchorEl(null);
  };

  const activeFilterCount = Object.values(formVal).reduce((count, value) => {
    if (Array.isArray(value)) {
      return count + (value.length > 0 ? 1 : 0);
    }

    return count + (value ? 1 : 0);
  }, 0);

  const handleOpenAddTaskDialog = () => {
    setAddTaskDialog({ open: true, data: { resposiblePersonId: user.userId } });
  };

  const commonTodoContentProps = {
    autoCompleteOptions,
    formValState: { formVal, setFormVal },
    prioValue,
    changed: { taskChanged, setTaskChanged },
    handleOpenAddTaskDialog,
  };

  const TodoContentMemo = useMemo(() => (p) => <TodoContent activeTab={activeTab} {...p} />, [activeTab]);

  const todoTabs = [
    {
      id: 0,
      name: 'Saját',
      content: <TodoContentMemo {...commonTodoContentProps} type={'own'} tabId={0} />,
    },
    ...(!roles.MYTODO_SEE_ONLY_OWN
      ? [
          {
            id: 1,
            name: 'Nyitott',
            content: <TodoContentMemo {...commonTodoContentProps} type={'opened'} tabId={1} />,
          },
          {
            id: 2,
            name: 'Összes',
            content: <TodoContentMemo {...commonTodoContentProps} type={'all'} tabId={2} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <Box
        sx={{
          minHeight: '100%',
          p: 2,
          bgcolor: 'grey.100',
        }}
      >
        <Stack direction="row" spacing={1}>
          <Box flex={1}>
            <TabsUI activeTab={activeTab} handleTabClick={handleTabClick}>
              {todoTabs.map((tab) => (
                <Box key={tab.id} name={tab.name}>
                  {!isMobile && tab.content}
                </Box>
              ))}
            </TabsUI>
          </Box>

          {isMobile && (
            <Stack direction="row" spacing={1}>
              <Box>
                <IconButton
                  disableRipple
                  onClick={handleOpenFilters}
                  sx={{ backgroundColor: '#78b8ef', color: '#fff', borderRadius: 2, p: 0.4 }}
                >
                  <Badge badgeContent={activeFilterCount} color="secondary">
                    <FilterAltIcon fontSize="large" />
                  </Badge>
                </IconButton>
              </Box>
              <Box>
                <IconButton
                  disableRipple
                  onClick={handleOpenAddTaskDialog}
                  sx={{ backgroundColor: '#0fbd1d', color: '#fff', borderRadius: 2, p: 0.4 }}
                >
                  <AddIcon fontSize="large" />
                </IconButton>
              </Box>
            </Stack>
          )}
        </Stack>

        <Popover
          id="todo-filter-popover"
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilters}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                width: 360,
                p: 2,
              },
            },
          }}
        >
          <TodoFilters
            formValState={{ formVal, setFormVal }}
            autoCompleteOptions={autoCompleteOptions}
            prioValue={prioValue}
          />
        </Popover>

        {isMobile && (
          <Box
            sx={{
              width: '100%',
            }}
          >
            {todoTabs.find((tab) => tab.id === activeTab)?.content}
          </Box>
        )}
      </Box>

      <SimpleTaskPhoneFormDialog
        dialogData={addTaskDialog}
        handleClose={() => setAddTaskDialog({ open: false, data: {} })}
        changed={{ taskChanged, setTaskChanged }}
      />
    </>
  );
};

export default MyTodoTable;
